import { debounce } from 'lodash';
import { useParams } from 'react-router';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, ReactNode, useCallback } from 'react';

import { LoadingButton } from '@mui/lab';
import { Box, Stack } from '@mui/system';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { Card, Chip, Avatar, Tooltip, CardHeader, AvatarGroup } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { ListEntityType } from 'src/services/lists/lists.types';
import { openMultiMailerDialog } from 'src/store/slices/app/appSlice';
import { useGetListByIdQuery, useRemoveListItemsMutation } from 'src/services/lists/lists.service';
import { MailerTargetType, EmailMessageTemplateType } from 'src/services/email-sync/email-sync.types';

import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import DataListActionsMenu from 'src/components/data-list/data-list-actions-menu';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

import AddToListModal from 'src/sections/lists/modals/add-to-list-modal';
import ListEditorModal from 'src/sections/lists/modals/list-editor-modal';

import { ListContactsTable, ListCompaniesTable, ListCandidatesTable } from './list-items-table';

/** Only candidate and contact lists have a mailable entity. */
const MAILABLE = [ListEntityType.CANDIDATE, ListEntityType.CONTACT];

/**
 * A saved list on the shared DataList.
 *
 * The rows come from the primary lean endpoints scoped by `list_id`, so each variant reuses the
 * matching column registry and inherits its configurable columns, per-column search, server sort and
 * infinite scroll. The old table had none of those, and its search box was inert at both ends — the
 * frontend built a filterQuery it never sent and the backend accepted a search_query it never used.
 *
 * Selection now holds ENTITY ids rather than list_item ids, which is what the mailer expects; the
 * previous "mail selection" was passing the wrong kind.
 */
export default function ListsDetailView() {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const { list_id: listId } = useParams();

  const listEditorControl = useBoolean();
  const addToListControl = useBoolean();
  const removeConfirmControl = useBoolean();

  const [searchInput, setSearchInput] = useState('');
  const [filterQuery, setFilterQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { data: list } = useGetListByIdQuery(listId as string, { skip: !listId });
  const [removeItems, { isLoading: isRemoving }] = useRemoveListItemsMutation();

  const applySearch = useMemo(() => debounce((value: string) => setFilterQuery(value), 300), []);
  const onSearchChange = useCallback((value: string) => { setSearchInput(value); applySearch(value); }, [applySearch]);

  const onSelectRow = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);
  const onSelectAll = useCallback((checked: boolean, ids: string[]) => setSelectedIds(checked ? Array.from(new Set(ids)) : []), []);

  /** Takes ENTITY ids — the remove endpoint resolves them to this list's memberships. */
  const handleRemoveFromList = useCallback(async (entityIds: string[]) => {
    try {
      await removeItems({ list_id: listId as string, data: { entities: entityIds } }).unwrap();
      setSelectedIds((prev) => prev.filter((id) => !entityIds.includes(id)));
      removeConfirmControl.onFalse();
      enqueueSnackbar(t('lists.api.remove_from_list.success'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('lists.api.remove_from_list.default_error'), { variant: 'error' });
    }
  }, [removeItems, listId, t, removeConfirmControl]);

  const mailerTypes = useCallback(() => {
    switch (list?.entity_type) {
      case ListEntityType.CANDIDATE:
        return { templateType: EmailMessageTemplateType.CANDIDATE_ENGAGEMENT, targetType: MailerTargetType.CANDIDATE };
      case ListEntityType.CONTACT:
        return { templateType: EmailMessageTemplateType.CONTACT_ENGAGEMENT, targetType: MailerTargetType.CONTACT };
      default:
        return null;
    }
  }, [list]);

  const handleMailWholeList = useCallback(() => {
    const types = mailerTypes();
    if (!list || !types) return;
    dispatch(openMultiMailerDialog({ listId: list.id, ...types }));
  }, [dispatch, list, mailerTypes]);

  const handleMailSelection = useCallback(() => {
    const types = mailerTypes();
    if (!types || !selectedIds.length) return;
    dispatch(openMultiMailerDialog({ entities: selectedIds, ...types }));
  }, [dispatch, mailerTypes, selectedIds]);

  const hasSelection = selectedIds.length > 0;
  const canMail = !!list && MAILABLE.includes(list.entity_type);

  // Takes the mounted variant's own column picker rather than owning it, so no picker state crosses
  // a component boundary.
  const renderToolbar = (columnPicker: ReactNode) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
      <TextField
        size="small"
        value={searchInput}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={t('common.placeholders.search')}
        InputProps={{ startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-fill" /></InputAdornment> }}
        sx={{ width: 260 }}
      />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {columnPicker}
        <DataListActionsMenu
          actions={[
            { key: 'add-items', label: t('lists.tooltips.add_items'), icon: 'material-symbols:post-add', onClick: addToListControl.onTrue },
            { key: 'mail-list', label: t('lists.tooltips.send_bulk_mail'), icon: 'iconoir:send-mail', onClick: handleMailWholeList, hidden: !canMail },
            { key: 'mail-selection', label: t('lists.tooltips.send_selective_bulk_mail'), icon: 'fluent:mail-list-28-regular', onClick: handleMailSelection, hidden: !canMail, disabled: !hasSelection },
            { key: 'remove-selected', label: t('lists.actions.remove_from_list'), icon: 'ix:remove-circle-filled', onClick: removeConfirmControl.onTrue, disabled: !hasSelection, color: 'error.main' },
          ]}
        />
      </Stack>
    </Stack>
  );

  const variantProps = {
    listId: listId as string,
    filterQuery,
    isTyping: searchInput !== filterQuery,
    selectedIds,
    onSelectRow,
    onSelectAll,
    onRemoveFromList: handleRemoveFromList,
    isRemoving,
    renderToolbar,
  };

  return (
    <Stack direction="column" spacing={2}>
      <Card variant="outlined" sx={{ pb: 2 }}>
        <CardHeader
          action={
            <Tooltip title={t('lists.tooltips.edit_list')}>
              <Box component="span">
                <LoadingButton
                  variant="text"
                  startIcon={<Iconify icon="cuida:edit-outline" />}
                  onClick={listEditorControl.onTrue}
                >
                  {t('common.edit')}
                </LoadingButton>
              </Box>
            </Tooltip>
          }
          title={
            <Stack direction="row" alignItems="start" justifyContent="start" gap={1}>
              <Box>
                <TruncatedTextField
                  text={list?.name ?? 'Loading...'}
                  mode="characters"
                  limit={25}
                  showMoreEnabled={false}
                  variant="h6"
                  fontWeight="bold"
                  gutterBottom
                  style={{ textTransform: 'capitalize' }}
                />
              </Box>
              {list && <Chip label={t(`lists.entity_list_type.${list.entity_type}`)} size="small" variant="outlined" />}
            </Stack>
          }
          subheader={
            <Stack direction="column" alignItems="start" gap={1}>
              <TruncatedTextField
                text={list?.description}
                mode="lines"
                limit={2}
                showMoreEnabled
                variant="caption"
                gutterBottom
              />

              <AvatarGroup max={5} sx={{ flexWrap: 'wrap' }} spacing="medium">
                {list?.access_list.map((account) => (
                  <Tooltip key={account.id} title={`${account.first_name} ${account.last_name}`}>
                    <Avatar sizes="small" alt={account.first_name}>
                      {account?.first_name?.charAt(0).toUpperCase()}
                    </Avatar>
                  </Tooltip>
                ))}
              </AvatarGroup>
            </Stack>
          }
        />
      </Card>

      <Card>
        {list?.entity_type === ListEntityType.CONTACT && <ListContactsTable {...variantProps} />}
        {list?.entity_type === ListEntityType.CANDIDATE && <ListCandidatesTable {...variantProps} />}
        {list?.entity_type === ListEntityType.COMPANY && <ListCompaniesTable {...variantProps} />}

        <AddToListModal open={addToListControl.value} onClose={addToListControl.onFalse} listId={listId as string} />
        <ListEditorModal
          open={listEditorControl.value}
          onClose={listEditorControl.onFalse}
          entityType={list?.entity_type as ListEntityType}
          listId={list?.id}
        />

        <ConfirmDialog
          open={removeConfirmControl.value}
          onClose={removeConfirmControl.onFalse}
          title={t('lists.actions.remove_from_list')}
          content={t('lists.actions.remove_from_list_confirm', { count: selectedIds.length })}
          action={
            <LoadingButton
              variant="contained"
              color="error"
              loading={isRemoving}
              onClick={() => handleRemoveFromList(selectedIds)}
            >
              {t('common.remove')}
            </LoadingButton>
          }
        />
      </Card>
    </Stack>
  );
}
